const VALID_SIDES = new Set(['left', 'right'])

function finitePoint(point) {
    if (!point || !Number.isFinite(Number(point.x)) || !Number.isFinite(Number(point.y))) return null
    return {
        x: Number(point.x),
        y: Number(point.y),
    }
}

export function normalizeConnectionRoute(route) {
    if (!route || typeof route !== 'object') return null

    const normalized = {
        waypoints: Array.isArray(route.waypoints)
            ? route.waypoints.map(finitePoint).filter(Boolean)
            : [],
    }

    if (VALID_SIDES.has(route.fromSide)) normalized.fromSide = route.fromSide
    if (VALID_SIDES.has(route.toSide)) normalized.toSide = route.toSide

    return normalized.waypoints.length || normalized.fromSide || normalized.toSide
        ? normalized
        : null
}

export function cloneConnectionRoute(route) {
    const normalized = normalizeConnectionRoute(route)
    return normalized ? {
        ...normalized,
        waypoints: normalized.waypoints.map(point => ({ ...point })),
    } : null
}

export function setConnectionRoute(field, route) {
    if (!field || typeof field !== 'object') return null

    const normalized = cloneConnectionRoute(route)
    field.connection_route = normalized
    return normalized
}

export function connectionReferenceChanged(previous = {}, current = {}) {
    const normalizeValue = value => value === undefined || value === null ? '' : String(value)

    return Boolean(previous.fk) !== Boolean(current.fk)
        || normalizeValue(previous.fk_table) !== normalizeValue(current.fk_table)
        || normalizeValue(previous.fk_field) !== normalizeValue(current.fk_field)
}

export function automaticConnectionDirection(fromRow, toRow) {
    if (fromRow.right <= toRow.left) return ['right', 'left']
    if (fromRow.left >= toRow.right) return ['left', 'right']
    if (fromRow.right <= toRow.right) return ['right', 'right']
    return ['left', 'left']
}

export function connectionDirection(fromRow, toRow, route = null) {
    const automatic = automaticConnectionDirection(fromRow, toRow)
    const normalized = normalizeConnectionRoute(route)
    return [
        normalized?.fromSide || automatic[0],
        normalized?.toSide || automatic[1],
    ]
}

export function assignConnectionLanes(connections) {
    const groupsByRow = new Map()

    for (const connection of connections) {
        const [, targetSide] = connection.direction()
        let sideGroups = groupsByRow.get(connection.toRow)
        if (!sideGroups) {
            sideGroups = new Map()
            groupsByRow.set(connection.toRow, sideGroups)
        }

        const group = sideGroups.get(targetSide) || []
        group.push(connection)
        sideGroups.set(targetSide, group)
    }

    for (const sideGroups of groupsByRow.values()) {
        for (const group of sideGroups.values()) {
            group.sort((first, second) => {
                const firstTable = first.fromRow.table
                const secondTable = second.fromRow.table
                return firstTable.top - secondTable.top
                    || firstTable.left - secondTable.left
                    || first.fromRow.top - second.fromRow.top
                    || String(first.fromRow.data?.name || '').localeCompare(String(second.fromRow.data?.name || ''))
            })

            group.forEach((connection, index) => {
                connection.setLane(index, group.length)
            })
        }
    }

    return connections
}

export function distanceToSegment(point, start, end) {
    const dx = end.x - start.x
    const dy = end.y - start.y
    if (dx === 0 && dy === 0) return Math.hypot(point.x - start.x, point.y - start.y)

    const position = Math.max(0, Math.min(1,
        ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)
    ))
    const closestX = start.x + position * dx
    const closestY = start.y + position * dy
    return Math.hypot(point.x - closestX, point.y - closestY)
}

export function nearestSegmentIndex(point, points) {
    let nearestIndex = 0
    let nearestDistance = Number.POSITIVE_INFINITY

    for (let index = 0; index < points.length - 1; index++) {
        const distance = distanceToSegment(point, points[index], points[index + 1])
        if (distance < nearestDistance) {
            nearestDistance = distance
            nearestIndex = index
        }
    }

    return nearestIndex
}

export function cubicBezierPoint(start, controlOne, controlTwo, end, progress) {
    const inverse = 1 - progress
    return {
        x: inverse ** 3 * start.x
            + 3 * inverse ** 2 * progress * controlOne.x
            + 3 * inverse * progress ** 2 * controlTwo.x
            + progress ** 3 * end.x,
        y: inverse ** 3 * start.y
            + 3 * inverse ** 2 * progress * controlOne.y
            + 3 * inverse * progress ** 2 * controlTwo.y
            + progress ** 3 * end.y,
    }
}

export function sampleBezier(start, controlOne, controlTwo, end, segments = 24) {
    const points = []
    for (let index = 0; index <= segments; index++) {
        points.push(cubicBezierPoint(start, controlOne, controlTwo, end, index / segments))
    }
    return points
}
